/**
 * Settings — the operator surfaces that are real API families but are not things anyone
 * arrives *wanting*. Team, connections, keys, MCP policy and detectors each have routes and
 * each would be its own nav item in an API-shaped console. You come here when something needs
 * changing; the screens before it are where the work is.
 *
 * Three of these had no surface anywhere before this build:
 *
 *  - **Rotation.** It existed only behind the platform master key — ours — so the one thing a
 *    tenant most needs to do without asking us was the one thing they could not.
 *  - **`POST /agent/mcp-credential`.** The on-ramp the MCP firewall was missing: the firewall
 *    demands a 300-second passport, and a passport cannot live in a static `mcpServers` JSON
 *    block a client reads once at startup. Without it the firewall is configurable and
 *    unreachable from a real client.
 *
 * **Not here: the background sweeps.** `GET /health/reaper` needs the platform MASTER key, and
 * a tenant does not have one — so a panel for it would 403 on every load forever. That gap is
 * real and it belongs to whoever runs the platform, not to a customer looking at their own
 * fleet; a permanently-broken card here would answer their question wrongly rather than not
 * answering it. Expiry self-heals on the next call either way.
 */

import { useState } from "react";
import { ScrollX, State, VerdictChip, useAsync } from "@authoxi/ui-shared";
import type { Api } from "../api";

export function Settings({ api }: { api: Api }) {
  return (
    <>
      <div className="view-head">
        <span className="label">Settings</span>
        <h1>Settings</h1>
        <p>
          Everything here is configuration. If you are looking at this screen, something needs
          changing — otherwise the screens before it are where the work is.
        </p>
      </div>

      <People api={api} />
      <Connections api={api} />
      <Firewall api={api} />
      <Keys api={api} />
      <Detectors api={api} />
    </>
  );
}

function People({ api }: { api: Api }) {
  const members = useAsync(() => api.members());
  return (
    <>
      <div className="rule">
        <span className="label">People</span>
      </div>
      <div className="card pad">
        {members.loading && <State kind="loading" title="Loading…" />}
        {members.data?.length === 0 && (
          <State
            kind="empty"
            title="No members yet."
            detail="The tenant key alone cannot say WHO is holding it — which is the gap this closes."
          />
        )}
        {members.data && members.data.length > 0 && (
          <ScrollX>
            <table className="table" data-testid="members">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {members.data.map((m) => (
                  <tr key={m.member_id}>
                    <td className="mono">{m.email}</td>
                    {/* The roles the product actually has. An early design mockup said
                        "operator" and "approver", which do not exist — a dropdown offering
                        them is a dropdown the API rejects. */}
                    <td>
                      <span className="pill">{m.role}</span>
                    </td>
                    <td className="sm muted">{m.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        )}
        <p className="xs muted" style={{ marginTop: "var(--s3)" }}>
          Every role change is written to an admin audit log operators cannot edit. When SCIM is
          on, this table becomes read-only and your directory is the source of truth.
        </p>
      </div>
    </>
  );
}

function Connections({ api }: { api: Api }) {
  const connections = useAsync(() => api.connections());
  return (
    <>
      <div className="rule" style={{ marginTop: "var(--s7)" }}>
        <span className="label">Connected systems</span>
      </div>
      <div className="card pad">
        {connections.data?.length === 0 && (
          <State kind="empty" title="Nothing connected." detail="Declare a system and agents can be scoped to it before the secret exists." />
        )}
        <div className="stack">
          {(connections.data ?? []).map((c) => (
            <div className="between" key={c.provider}>
              <span className="mono sm">{c.provider}</span>
              <span className="sm muted">
                {c.mode === "self_hosted" ? "declared — the secret never reaches us" : "sealed under your KMS"}
              </span>
              <VerdictChip value={c.status === "active" ? "allow" : "deny"} dim />
            </div>
          ))}
        </div>
        <p className="xs muted" style={{ marginTop: "var(--s3)" }}>
          <strong>Never a key vault.</strong> Agents never receive these credentials. The
          enforcement point holds them — inside your boundary if you self-host, sealed under your
          KMS if you do not.
        </p>
      </div>
    </>
  );
}

function Firewall({ api }: { api: Api }) {
  const policies = useAsync(() => api.mcpPolicies());
  const agents = useAsync(() => api.agents());
  const mandates = useAsync(() => api.mandates({ subject_kind: "agent" }));
  const [minted, setMinted] = useState<Record<string, string> | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // A credential is minted FOR a mandate — the authority is what the token carries. An agent
  // holding none has nothing to mint against, and offering it anyway produces a 4xx the
  // operator has to decode.
  const live = (mandates.data ?? []).filter((m) => m.status === "active");

  return (
    <>
      <div className="rule" style={{ marginTop: "var(--s7)" }}>
        <span className="label">MCP firewall</span>
      </div>
      <div className="card pad">
        {policies.data?.length === 0 && (
          <State kind="empty" title="No tools classified." detail="An unclassified tool is never a free pass — it is refused." />
        )}
        {policies.data && policies.data.length > 0 && (
          <ScrollX>
            <table className="table" data-testid="tool-policies">
              <thead>
                <tr>
                  <th>Server</th>
                  <th>Tool</th>
                  <th>Classified as</th>
                </tr>
              </thead>
              <tbody>
                {policies.data.map((p) => (
                  <tr key={`${p.server}:${p.tool}`}>
                    <td className="mono">{p.server}</td>
                    <td className="mono">{p.tool}</td>
                    <td className="mono sm">{p.action_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        )}
        {/* No "Seen" column: the firewall passes the action CLASSIFICATION to decide(), never
            server:tool, so per-tool call counts do not exist anywhere. A column of invented
            numbers on the screen that configures enforcement would be the worst place for one. */}
        <p className="xs muted" style={{ marginTop: "var(--s3)" }}>
          The firewall reaches the same verdict as the API because it calls the same predicate —
          not a second copy of the rules that drifts from the first.
        </p>

        <div className="hr" />
        <p className="label">Connect an MCP client</p>
        <p className="sm" style={{ margin: "var(--s2) 0 var(--s3)", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
          A passport lives 300 seconds and cannot sit in a static <span className="mono">mcpServers</span>{" "}
          block a client reads once at startup. This mints a long-lived credential that can — no
          change to the agent's source, and every tool call still goes through the firewall.
        </p>
        {minted ? (
          <>
            {/* The config headers, not the raw token: what an operator pastes into
                `mcpServers` is a JSON block, and handing them a bare string means they get to
                guess the header name. */}
            <pre className="code" style={{ wordBreak: "break-all" }} data-testid="mcp-credential">
              {JSON.stringify(minted, null, 2)}
            </pre>
            <button className="btn" onClick={() => setMinted(null)}>
              Done
            </button>
          </>
        ) : (
          <form
            className="row"
            data-testid="mint-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget as HTMLFormElement;
              const mandateId = (form.elements.namedItem("mandate") as HTMLSelectElement).value;
              const mandate = live.find((m) => m.mandate_id === mandateId);
              if (!mandate) return setErr("Pick a mandate first.");
              setErr(null);
              try {
                setMinted(
                  (
                    await api.mintMcpCredential({
                      agent_id: mandate.subject_id,
                      mandate_id: mandate.mandate_id,
                    })
                  ).config_headers,
                );
              } catch (e) {
                setErr(e instanceof Error ? e.message : String(e));
              }
            }}
          >
            <select className="input" name="mandate" data-testid="mint-mandate">
              {live.map((m) => {
                const agent = (agents.data ?? []).find((a) => a.agent_id === m.subject_id);
                return (
                  <option value={m.mandate_id} key={m.mandate_id}>
                    {agent?.name ?? m.subject_id} — {m.allowed_actions.join(", ") || "no actions"}
                  </option>
                );
              })}
            </select>
            <button className="btn btn-go" type="submit" disabled={live.length === 0}>
              Mint a credential
            </button>
          </form>
        )}
        {live.length === 0 && (
          <p className="sm muted" style={{ marginTop: "var(--s2)" }}>
            No agent holds an active mandate yet. A credential carries the authority inside it,
            so there has to be some.
          </p>
        )}
        {err && <p className="sm" style={{ color: "var(--deny-text)" }}>{err}</p>}
      </div>
    </>
  );
}

function Keys({ api }: { api: Api }) {
  const tenant = useAsync(() => api.tenant());
  const [armed, setArmed] = useState(false);
  const [rotated, setRotated] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  return (
    <>
      <div className="rule" style={{ marginTop: "var(--s7)" }}>
        <span className="label">Keys</span>
      </div>
      <div className="card pad">
        <dl className="kv">
          <dt>publishable</dt>
          <dd className="mono">{tenant.data?.publishable_key ?? "…"}</dd>
          <dt>secret</dt>
          <dd className="muted">unrecoverable by design — rotate instead</dd>
        </dl>

        {rotated ? (
          <div style={{ marginTop: "var(--s4)" }} data-testid="rotated">
            <p className="label" style={{ color: "var(--deny-text)" }}>
              Your new secret key — shown once
            </p>
            <pre className="code" style={{ wordBreak: "break-all" }}>{rotated}</pre>
            <p className="sm" style={{ color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
              The old key stopped working the moment this one was issued. Update it wherever it
              is deployed. Nothing can recover this — here or anywhere.
            </p>
          </div>
        ) : (
          <>
            <button
              className="btn"
              style={{ marginTop: "var(--s4)" }}
              data-testid="rotate"
              onClick={async () => {
                // Two-click, and the confirm says what actually happens. There is no grace
                // window: a compromised key that keeps working for an hour is a delay, not a
                // rotation — so the operator has to be told, not protected by a window.
                if (!armed) return setArmed(true);
                setErr(null);
                try {
                  const r = await api.rotateSecret();
                  setRotated(r.secret_key);
                } catch (e) {
                  setErr(e instanceof Error ? e.message : String(e));
                } finally {
                  setArmed(false);
                }
              }}
            >
              {armed ? "Confirm — the current key stops working immediately" : "Rotate secret key"}
            </button>
            {err && <p className="sm" style={{ color: "var(--deny-text)" }}>{err}</p>}
          </>
        )}

        <p className="xs muted" style={{ marginTop: "var(--s3)" }}>
          This key can <strong>issue</strong> authority. It never belongs in an agent — agents
          hold a passport, which can only ask.
        </p>
      </div>
    </>
  );
}

function Detectors({ api }: { api: Api }) {
  const detectors = useAsync(() => api.detectors());
  return (
    <>
      <div className="rule" style={{ marginTop: "var(--s7)" }}>
        <span className="label">Anomaly detectors</span>
      </div>
      <div className="card pad">
        <p className="xs muted" style={{ margin: "0 0 var(--s4)" }}>
          Third parties that can signal “this agent is behaving oddly”. A signal is signed, so we
          know who raised it — and it can stop an agent without giving them a key of yours.
        </p>
        {detectors.data?.length === 0 && (
          <State kind="empty" title="None registered." detail="Nothing can currently raise a signal about your fleet." />
        )}
        <div className="stack" data-testid="detectors">
          {(detectors.data ?? []).map((d) => (
            <div className="between" key={d.detector_id}>
              <span className="mono sm">{d.name}</span>
              <VerdictChip value={d.status === "active" ? "allow" : "deny"} dim />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
